import { Preferences } from '@capacitor/preferences'
import {
  IMPERSONATION_RESTORE_KEY,
  parseImpersonationRestore,
  serializeImpersonationRestore,
  type ImpersonationRestore,
} from './impersonationRestore'

export type ImpersonationRestoreStorePorts = {
  getNative: () => Promise<string | null>
  setNative: (raw: string) => Promise<void>
  removeNative: () => Promise<void>
  getWeb: () => string | null
  setWeb: (raw: string) => void
  removeWeb: () => void
}

export type ImpersonationRestoreStore = {
  read: () => Promise<ImpersonationRestore | null>
  readWebSync: () => ImpersonationRestore | null
  write: (restore: ImpersonationRestore) => Promise<void>
  clear: () => Promise<void>
}

export function defaultImpersonationRestoreStorePorts (): ImpersonationRestoreStorePorts {
  return {
    async getNative () {
      try {
        const { value } = await Preferences.get({ key: IMPERSONATION_RESTORE_KEY })
        return value
      } catch {
        return null
      }
    },
    async setNative (raw) {
      try {
        await Preferences.set({ key: IMPERSONATION_RESTORE_KEY, value: raw })
      } catch {
        /* Preferences is native-only; ignore on web and in unit tests. */
      }
    },
    async removeNative () {
      try {
        await Preferences.remove({ key: IMPERSONATION_RESTORE_KEY })
      } catch {
        /* Preferences is native-only; ignore on web and in unit tests. */
      }
    },
    getWeb () {
      try {
        return globalThis.localStorage?.getItem(IMPERSONATION_RESTORE_KEY) ?? null
      } catch {
        return null
      }
    },
    setWeb (raw) {
      try {
        globalThis.localStorage.setItem(IMPERSONATION_RESTORE_KEY, raw)
      } catch {
        return
      }
    },
    removeWeb () {
      try {
        globalThis.localStorage.removeItem(IMPERSONATION_RESTORE_KEY)
      } catch {
        return
      }
    },
  }
}

export function createImpersonationRestoreStore (
  ports: ImpersonationRestoreStorePorts = defaultImpersonationRestoreStorePorts(),
): ImpersonationRestoreStore {
  return {
    readWebSync () {
      return parseImpersonationRestore(ports.getWeb())
    },
    async read () {
      const native = parseImpersonationRestore(await ports.getNative())
      if (native !== null) {
        return native
      }
      return parseImpersonationRestore(ports.getWeb())
    },
    async write (restore) {
      const existing = await this.read()
      if (existing !== null) {
        throw new Error('Already impersonating.')
      }
      const raw = serializeImpersonationRestore(restore)
      await ports.setNative(raw)
      ports.setWeb(raw)
    },
    async clear () {
      await ports.removeNative()
      ports.removeWeb()
    },
  }
}
